import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../../context/GameContext.jsx";
import { useLanguage } from "../../hooks/useLanguage.js";
import { ROLE_DATA } from "../../data/roles.js";
import { fadeUp, scaleIn } from "../../animations/variants.js";
import { Btn } from "../ui/Btn.jsx";
import { Panel, NightPanel, SectionTitle, DayHeader, NightHeader } from "../ui/Panel.jsx";
import { PlayerSelectCard } from "../player/PlayerSelectCard.jsx";
import { PlayerVoteCard } from "../player/PlayerVoteCard.jsx";
import { ClickablePlayer } from "../player/ClickablePlayer.jsx";
import { NightEventLog } from "../night/NightEventLog.jsx";
import {
  ProtectorActionView, WerewolfActionView, FatherOfWolvesActionView, WitchActionView,
  CupidActionView, FlutePlayerActionView, SeerActionView, RavenActionView,
  ThiefActionView, WildChildActionView, BigBadWolfActionView, WhiteWerewolfActionView,
  FoxActionView, ShepherdActionView,
} from "../night/NightActionViews.jsx";

// ── Mayor Election ───────────────────────────────────────────────
export function MayorElectionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const alive = session.players.filter(p => p.alive);
  const [selected, setSelected] = useState(null);
  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" exit="exit" className="flex flex-col gap-4 px-4 pb-8">
      <Panel>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">👑</span>
          <div>
            <h2 className="text-amber-200 font-bold text-lg">{t("Élection du Maire","Mayor Election")}</h2>
            <p className="text-amber-600/70 text-xs">{t("Avant la première nuit, les villageois élisent leur maire.","Before the first night, villagers elect their mayor.")}</p>
          </div>
        </div>
        <p className="text-amber-400/70 text-sm mb-4">{t("Qui a été élu Maire par vote des joueurs ?","Who was elected Mayor by player vote?")}</p>
        <div className="flex flex-col gap-2">
          {alive.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
        </div>
      </Panel>
      <Btn variant="primary" onClick={() => dispatch({type:"ELECT_MAYOR",payload:selected})} disabled={!selected} className="w-full py-4">
        {t("👑 Proclamer le Maire","👑 Proclaim Mayor")}
      </Btn>
    </motion.div>
  );
}

// ── Night Resolution ──────────────────────────────────────────────
export function NightResolutionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const victimId = session?.nightVictim;
  const victim = session?.players.find(p => p.id===victimId);
  const pendingCount = session?.pendingDeaths?.length || 0;
  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" className="flex flex-col gap-4 px-4 pb-8">
      <NightPanel>
        <h2 className="text-indigo-200 font-bold text-center text-lg mb-4">{t("🌅 Fin de la nuit","🌅 Night ends")}</h2>
        <div className="flex flex-col gap-3">
          {victim ? (
            <div className="bg-red-950/40 border border-red-800/30 rounded-2xl p-3 text-center">
              <p className="text-red-300/50 text-xs">{t("Attaqué par les loups","Attacked by wolves")}</p>
              <div className="flex justify-center mt-1"><ClickablePlayer player={victim} className="text-red-200 font-bold text-base" /></div>
            </div>
          ) : (
            <div className="bg-emerald-950/30 border border-emerald-900/20 rounded-2xl p-3 text-center">
              <p className="text-emerald-400/60 text-sm">{t("Personne n'a été attaqué (ou sauvé).","Nobody was attacked (or saved).")}</p>
            </div>
          )}
          {pendingCount>0 && (
            <div className="bg-purple-950/30 border border-purple-800/20 rounded-2xl p-3 text-center">
              <p className="text-purple-300/60 text-xs">+ {pendingCount} {t("mort(s) supplémentaire(s)","additional death(s)")}</p>
            </div>
          )}
        </div>
        <p className="text-indigo-400/50 text-xs text-center mt-4">{t("Réveillez tout le village pour révéler les morts.","Wake the whole village to reveal the deaths.")}</p>
      </NightPanel>
      <Btn variant="primary" onClick={() => dispatch({type:"RESOLVE_NIGHT"})} className="w-full py-4 text-base">{t("☀️ Réveil du village","☀️ Wake the village")}</Btn>
    </motion.div>
  );
}

// ── Night Phase (router for action views) ────────────────────────
export function NightPhaseView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  if (!session) return null;
  if (session.phase !== "night" && session.phase !== "night_resolution") return null;

  const { roundNightActions, currentNightRoleIndex, night } = session;
  if (session.phase === "night_resolution") return <NightResolutionView />;

  const currentRoleId = roundNightActions[currentNightRoleIndex];

  return (
    <motion.div key={`night-${night}-role-${currentNightRoleIndex}`} variants={fadeUp} initial="initial" animate="animate" exit="exit" className="flex flex-col gap-4 px-4 pb-8">
      <NightPanel>
        <NightHeader night={night} t={t} />
        <div className="flex gap-1 mb-1">
          {roundNightActions.map((rId, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i<currentNightRoleIndex?"bg-indigo-400":i===currentNightRoleIndex?"bg-indigo-200":"bg-indigo-900/40"}`} />
          ))}
        </div>
        <p className="text-indigo-500/50 text-[10px]">{currentNightRoleIndex+1}/{roundNightActions.length} {t("rôles","roles")}</p>
      </NightPanel>
      <AnimatePresence mode="wait">
        <motion.div key={currentRoleId} variants={fadeUp} initial="initial" animate="animate" exit="exit">
          {!currentRoleId ? <NightResolutionView />
            : currentRoleId === "thief" ? <ThiefActionView />
            : currentRoleId === "cupid" ? <CupidActionView />
            : currentRoleId === "wild_child" ? <WildChildActionView />
            : currentRoleId === "seer" ? <SeerActionView />
            : currentRoleId === "fox" ? <FoxActionView />
            : currentRoleId === "protector" ? <ProtectorActionView />
            : currentRoleId === "shepherd" ? <ShepherdActionView />
            : currentRoleId === "werewolf" ? <WerewolfActionView />
            : currentRoleId === "father_of_wolves" ? <FatherOfWolvesActionView />
            : currentRoleId === "big_bad_wolf" ? <BigBadWolfActionView />
            : currentRoleId === "witch" ? <WitchActionView />
            : currentRoleId === "flute_player" ? <FlutePlayerActionView />
            : currentRoleId === "raven" ? <RavenActionView />
            : currentRoleId === "white_werewolf" ? <WhiteWerewolfActionView />
            : (
              <NightPanel>
                <p className="text-indigo-200 text-center">{currentRoleId} {t("se réveille.","wakes up.")}</p>
                <Btn variant="ghost" onClick={() => dispatch({type:"NEXT_NIGHT_ROLE"})} className="w-full mt-4">{t("Continuer","Continue")}</Btn>
              </NightPanel>
            )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

// ── Day Reveal ────────────────────────────────────────────────────
export function DayRevealView() {
  const { state, dispatch } = useGame();
  const { t, lang } = useLanguage();
  const session = state.session;
  const log = session?.nightLog || [];
  const source = session?.dayRevealSource || "night";
  const isAfterExecution = source === "execution";

  const deaths = (session?.newDeaths || []).map(id => session?.players.find(p=>p.id===id)?.name).filter(Boolean);
  const summaryText = deaths.length===0 ? t("Aucun mort cette nuit.","No deaths tonight.")
    : deaths.length===1 ? t(`${deaths[0]} est mort·e.`,`${deaths[0]} died.`)
    : t(`${deaths.slice(0,-1).join(", ")} et ${deaths[deaths.length-1]} sont morts.`,
        `${deaths.slice(0,-1).join(", ")} and ${deaths[deaths.length-1]} died.`);
  const isQuiet = deaths.length===0;

  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" exit="exit" className="flex flex-col gap-4 px-4 pb-8">
      <Panel>
        <DayHeader day={session?.day || 1} t={t} />
        <p className="text-amber-500/60 text-xs uppercase tracking-widest mb-3">
          {isAfterExecution ? t("Résultat de l'exécution","Execution result") : t("Événements de la nuit","Night events")}
        </p>
        <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}}
          className={`rounded-2xl p-4 text-center mb-3 border font-semibold text-base
            ${isQuiet ? "bg-emerald-950/40 border-emerald-800/30 text-emerald-300" : "bg-red-950/40 border-red-800/30 text-red-200"}`}>
          {isQuiet ? "🌿 " : "💀 "}{summaryText}
        </motion.div>
        <NightEventLog log={log} players={session?.players || []} lang={lang} />
      </Panel>
      <div className="bg-amber-950/20 border border-amber-900/20 rounded-2xl px-4 py-3">
        <p className="text-amber-700/60 text-xs text-center italic">
          {isAfterExecution ? t("Annoncez l'élimination aux joueurs.","Announce the elimination to the players.")
            : t("Annoncez ces événements au village.","Announce these events to the village.")}
        </p>
      </div>
      {isAfterExecution ? (
        <Btn variant="night" onClick={() => dispatch({type:"START_NIGHT"})} className="w-full py-4">{t("🌙 Passer à la nuit suivante","🌙 Start next night")}</Btn>
      ) : (
        <Btn variant="primary" onClick={() => dispatch({type:"START_DEBATE"})} className="w-full py-4">{t("💬 Commencer le débat","💬 Start debate")}</Btn>
      )}
    </motion.div>
  );
}

// ── Hunter Shot ───────────────────────────────────────────────────
export function HunterShotView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const [selected, setSelected] = useState(null);
  const targets = session?.players.filter(p => p.alive) || [];
  const hunterPlayer = session?.players.find(p => !p.alive && p.baseRoleId==="hunter" && (session?.newDeaths||[]).includes(p.id));
  const hunterName = hunterPlayer?.name || t("Le Chasseur","The Hunter");

  return (
    <motion.div variants={scaleIn} initial="initial" animate="animate" exit="exit" className="flex flex-col gap-4 px-4 pb-8">
      <div className="bg-gradient-to-b from-amber-950 to-stone-950 border border-amber-700/40 rounded-3xl p-6 text-center">
        <p className="text-amber-300 text-4xl mb-3">🏹</p>
        <h2 className="text-amber-200 font-bold text-xl mb-2">{t("Dernier Tir du Chasseur","Hunter's Final Shot")}</h2>
        <p className="text-amber-500/70 text-sm">
          {hunterPlayer
            ? <><ClickablePlayer player={hunterPlayer} className="text-amber-400" /> {t("est mort·e. Il·elle tire une dernière balle.","has died. They fire one final shot.")}</>
            : <>{hunterName} {t("est mort·e. Il·elle tire une dernière balle.","has died. They fire one final shot.")}</>}
        </p>
      </div>
      <Panel>
        <p className="text-amber-400/70 text-sm mb-3 text-center">{t("Qui le Chasseur élimine-t-il ?","Who does the Hunter eliminate?")}</p>
        <div className="flex flex-col gap-2">
          {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
        </div>
      </Panel>
      <Btn variant="danger" onClick={() => dispatch({type:"HUNTER_SHOOT",payload:selected})} disabled={!selected} className="w-full py-4">{t("🏹 Tirer","🏹 Shoot")}</Btn>
    </motion.div>
  );
}

// ── Debate ────────────────────────────────────────────────────────
export function DebateView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const alive = session?.players.filter(p => p.alive) || [];
  const current = alive[session?.currentDebateSpeaker || 0];
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" exit="exit" className="flex flex-col gap-4 px-4 pb-8">
      <Panel>
        <DayHeader day={session?.day || 1} t={t} />
        <div className="text-center mb-6">
          <p className="text-amber-500/60 text-xs uppercase tracking-widest mb-1">{t("Parole à","Speaking")}</p>
          <motion.div key={current?.id} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="text-amber-200 font-bold text-2xl">
            {current ? <ClickablePlayer player={current} className="text-2xl text-amber-200" /> : "—"}
          </motion.div>
          {current?.mayor && <span className="text-yellow-400 text-sm">👑 {t("Maire","Mayor")}</span>}
        </div>
        <div className="flex gap-1.5 justify-center mb-4">
          {alive.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-all
              ${i<(session?.currentDebateSpeaker||0)?"bg-amber-500":i===(session?.currentDebateSpeaker||0)?"bg-amber-200 scale-125":"bg-amber-900/40"}`} />
          ))}
        </div>
        <p className="text-amber-600/50 text-xs text-center">{(session?.currentDebateSpeaker||0)+1} / {alive.length}</p>
      </Panel>
      <Btn variant="primary" onClick={() => dispatch({type:"NEXT_DEBATE_SPEAKER"})} className="w-full py-4">
        {(session?.currentDebateSpeaker||0)+1 >= alive.length ? t("→ Voter","→ Vote") : t("Joueur suivant →","Next player →")}
      </Btn>
      {!showSkipConfirm ? (
        <Btn variant="ghost" onClick={() => setShowSkipConfirm(true)} className="w-full">{t("⏭ Passer au vote directement","⏭ Skip to vote")}</Btn>
      ) : (
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={() => setShowSkipConfirm(false)} className="flex-1">{t("Annuler","Cancel")}</Btn>
          <Btn variant="secondary" onClick={() => dispatch({type:"SKIP_TO_VOTE"})} className="flex-1">{t("Confirmer","Confirm")}</Btn>
        </div>
      )}
    </motion.div>
  );
}

// ── Voting ────────────────────────────────────────────────────────
export function VotingView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const allAlive = session?.players.filter(p => p.alive) || [];
  const revoteTiedIds = session?.revoteTiedIds || null;
  const voteTargets = revoteTiedIds ? allAlive.filter(p => revoteTiedIds.includes(p.id)) : allAlive;
  const [confirmId, setConfirmId] = useState(null);
  const [tieResolution, setTieResolution] = useState(null);

  const totalVotes = voteTargets.reduce((s,p) => s+p.voteCount, 0);
  const maxVotes = Math.max(...voteTargets.map(p => p.voteCount), 0);
  const topPlayers = voteTargets.filter(p => p.voteCount===maxVotes && maxVotes>0);
  const isTie = topPlayers.length > 1;

  const execute = (id) => { dispatch({type:"CLEAR_REVOTE"}); dispatch({type:"EXECUTE_PLAYER",payload:id}); setConfirmId(null); setTieResolution(null); };
  const startRevote = () => { dispatch({type:"REVOTE_TIED",payload:topPlayers.map(p=>p.id)}); setTieResolution(null); };
  const skipExecution = () => { dispatch({type:"CLEAR_REVOTE"}); dispatch({type:"RESET_VOTES"}); dispatch({type:"START_NIGHT"}); setTieResolution(null); };

  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" exit="exit" className="flex flex-col gap-4 px-4 pb-8">
      {revoteTiedIds && (
        <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} className="bg-amber-950/60 border border-amber-600/40 rounded-2xl px-4 py-3 text-center">
          <p className="text-amber-300 text-sm font-semibold">{t("🔁 Revote — Joueurs à égalité uniquement","🔁 Revote — Tied players only")}</p>
          <button onClick={() => { dispatch({type:"CLEAR_REVOTE"}); dispatch({type:"RESET_VOTES"}); }} className="text-amber-600/70 text-xs mt-1 active:scale-95">{t("Annuler → vote complet","Cancel → full vote")}</button>
        </motion.div>
      )}
      <Panel>
        <DayHeader day={session?.day || 1} t={t} />
        <p className="text-amber-400/70 text-sm mb-4">{t("Vote : qui est éliminé ?","Vote: who is eliminated?")}</p>
        <div className="flex flex-col gap-2">
          {voteTargets.map(p => <PlayerVoteCard key={p.id} player={p} onAdd={(id)=>dispatch({type:"ADD_VOTE",payload:id})} onRemove={(id)=>dispatch({type:"REMOVE_VOTE",payload:id})} />)}
        </div>
        {totalVotes>0 && <div className="mt-3 text-center"><p className="text-amber-500/50 text-xs">{totalVotes} {t("votes","votes")}</p></div>}
      </Panel>
      <Btn variant="ghost" onClick={() => dispatch({type:"RESET_VOTES"})} className="w-full">{t("🔄 Réinitialiser les votes","🔄 Reset votes")}</Btn>

      {topPlayers.length===1 && !confirmId && !tieResolution && (
        <Btn variant="danger" onClick={() => setConfirmId(topPlayers[0].id)} className="w-full py-4">
          {t(`⚖️ Éliminer ${topPlayers[0].name} (${maxVotes} vote${maxVotes>1?"s":""})`,`⚖️ Eliminate ${topPlayers[0].name} (${maxVotes} vote${maxVotes>1?"s":""})`)}
        </Btn>
      )}
      {isTie && !tieResolution && !confirmId && (
        <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} className="bg-amber-950/50 border border-amber-700/40 rounded-3xl p-4">
          <p className="text-amber-300 font-semibold text-center mb-1">{t("⚖️ Égalité !","⚖️ It's a tie!")}</p>
          <p className="text-amber-600/60 text-xs text-center mb-3">{topPlayers.map(p=>p.name).join(", ")} — {maxVotes} {t("vote(s) chacun","vote(s) each")}</p>
          <Btn variant="secondary" onClick={() => setTieResolution("menu")} className="w-full">{t("Résoudre l'égalité…","Resolve the tie…")}</Btn>
        </motion.div>
      )}
      {tieResolution==="menu" && (
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="bg-stone-950/80 border border-amber-800/30 rounded-3xl p-4 flex flex-col gap-3">
          <p className="text-amber-300 font-semibold text-center text-sm">{t("Comment résoudre l'égalité ?","How to resolve the tie?")}</p>
          <button onClick={startRevote} className="flex items-start gap-3 rounded-2xl p-3 border border-amber-700/30 bg-amber-950/30 active:bg-amber-950/60 text-left transition-all active:scale-98">
            <span className="text-xl shrink-0">🔁</span>
            <div>
              <p className="text-amber-200 text-sm font-semibold">{t("Revote (joueurs à égalité)","Revote (tied players only)")}</p>
              <p className="text-amber-600/60 text-xs">{t("Relancer le vote entre les joueurs à égalité uniquement.","Restart voting between tied players only.")}</p>
            </div>
          </button>
          <div className="flex flex-col gap-2">
            <p className="text-amber-500/60 text-xs text-center uppercase tracking-widest">{t("Élimination directe","Direct elimination")}</p>
            {topPlayers.map(p => (
              <button key={p.id} onClick={() => { setTieResolution(null); setConfirmId(p.id); }} className="flex items-center gap-3 rounded-2xl p-3 border border-red-800/30 bg-red-950/30 active:bg-red-950/60 text-left transition-all active:scale-98">
                <span className="text-xl">{ROLE_DATA[p.baseRoleId]?.emoji||"?"}</span>
                <p className="flex-1 text-red-200 text-sm font-semibold">{p.name}</p>
                <span className="text-red-400 text-xs">{t("Éliminer","Eliminate")}</span>
              </button>
            ))}
          </div>
          <button onClick={skipExecution} className="flex items-start gap-3 rounded-2xl p-3 border border-stone-700/30 bg-stone-900/30 active:bg-stone-900/60 text-left transition-all active:scale-98">
            <span className="text-xl shrink-0">⏭️</span>
            <div>
              <p className="text-amber-200/70 text-sm font-semibold">{t("Passer — Personne n'est éliminé","Skip — Nobody is eliminated")}</p>
              <p className="text-amber-700/40 text-xs">{t("Le village ne s'est pas mis d'accord. La nuit arrive.","The village couldn't agree. Night falls.")}</p>
            </div>
          </button>
          <Btn variant="ghost" onClick={() => setTieResolution(null)} className="w-full text-xs">{t("← Retour","← Back")}</Btn>
        </motion.div>
      )}
      {confirmId && (
        <motion.div initial={{opacity:0,scale:0.9}} animate={{opacity:1,scale:1}} className="bg-red-950/50 border border-red-700/50 rounded-3xl p-4">
          <p className="text-red-200 text-center font-semibold mb-3">
            {t(`Confirmer l'élimination de ${session?.players.find(p=>p.id===confirmId)?.name} ?`,`Confirm eliminating ${session?.players.find(p=>p.id===confirmId)?.name}?`)}
          </p>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => { setConfirmId(null); setTieResolution(null); }} className="flex-1">{t("Annuler","Cancel")}</Btn>
            <Btn variant="danger" onClick={() => execute(confirmId)} className="flex-1">{t("Confirmer ⚖️","Confirm ⚖️")}</Btn>
          </div>
        </motion.div>
      )}
      {maxVotes===0 && !tieResolution && !confirmId && (
        <Btn variant="ghost" onClick={skipExecution} className="w-full">{t("⏭ Pas de vote — Passer à la nuit","⏭ No vote — Skip to night")}</Btn>
      )}
    </motion.div>
  );
}

// ── Game Over ─────────────────────────────────────────────────────
export function GameOverView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const winner = session?.winner;

  const configs = {
    village:       { emoji:"🏡", gradient:"from-emerald-900 to-emerald-950", border:"border-emerald-600",
                     fr:"Le Village Triomphe !", en:"The Village Triumphs!",
                     subFr:"Les Loups-Garous ont été éliminés.", subEn:"The Werewolves have been eliminated." },
    werewolf:      { emoji:"🐺", gradient:"from-red-950 to-stone-950", border:"border-red-700",
                     fr:"Les Loups Gagnent !", en:"The Werewolves Win!",
                     subFr:"La nuit appartient aux loups.", subEn:"The night belongs to the wolves." },
    lovers:        { emoji:"💕", gradient:"from-pink-950 to-rose-950", border:"border-pink-600",
                     fr:"Les Amoureux Gagnent !", en:"The Lovers Win!",
                     subFr:"L'amour a tout surpassé.", subEn:"Love conquered all." },
    flute_player:  { emoji:"🪗", gradient:"from-teal-950 to-cyan-950", border:"border-teal-600",
                     fr:"Le Joueur de Flûte Gagne !", en:"The Flute Player Wins!",
                     subFr:"Tous sont sous son charme.", subEn:"All are under his spell." },
    angel:         { emoji:"😇", gradient:"from-sky-950 to-slate-950", border:"border-sky-400",
                     fr:"L'Ange Gagne !", en:"The Angel Wins!",
                     subFr:"L'Ange a été éliminé lors du premier vote.", subEn:"The Angel was voted out on the first day." },
    white_werewolf:{ emoji:"🤍", gradient:"from-slate-900 to-stone-950", border:"border-white/30",
                     fr:"Le Loup Blanc Gagne !", en:"The White Werewolf Wins!",
                     subFr:"Il est le dernier survivant.", subEn:"He is the last one standing." },
    nobody:        { emoji:"💀", gradient:"from-stone-950 to-black", border:"border-stone-700",
                     fr:"Personne ne Survit…", en:"Nobody Survives…",
                     subFr:"Le village est en ruines.", subEn:"The village lies in ruins." },
  };
  const cfg = configs[winner] || configs.nobody;

  return (
    <motion.div variants={scaleIn} initial="initial" animate="animate" exit="exit" className="flex flex-col gap-4 px-4 pb-8">
      <div className={`bg-gradient-to-b ${cfg.gradient} border ${cfg.border} rounded-3xl p-8 text-center shadow-2xl`}>
        <motion.div animate={{scale:[1,1.15,1]}} transition={{duration:1.5,repeat:Infinity}} className="text-6xl mb-4">{cfg.emoji}</motion.div>
        <h2 className="text-white font-bold text-2xl mb-2">{t(cfg.fr, cfg.en)}</h2>
        <p className="text-white/50 text-sm">{t(cfg.subFr, cfg.subEn)}</p>
      </div>
      <Panel>
        <SectionTitle>{t("Rôles révélés","Roles revealed")}</SectionTitle>
        <div className="flex flex-col gap-2">
          {session?.players.map(p => {
            const role = ROLE_DATA[p.baseRoleId];
            const isRogueWC = p.baseRoleId==="wild_child" && p.effectiveAlignment==="werewolf";
            return (
              <div key={p.id} className={`flex items-center gap-3 rounded-xl p-2 ${p.alive?"":"opacity-50"}`}>
                <span className="text-lg">{role?.emoji}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-1 flex-wrap">
                    <ClickablePlayer player={p} className="text-sm text-amber-100" />
                    {p.statuses.includes("mayor") && <span className="text-xs">👑</span>}
                    {p.statuses.includes("lover") && <span className="text-xs">💕</span>}
                    {p.statuses.includes("infected") && <span className="text-[10px] bg-purple-950 border border-purple-700 rounded px-1 text-purple-300">🩸</span>}
                    {isRogueWC && <span className="text-[10px] bg-red-950 border border-red-800 rounded px-1 text-red-300">🐺</span>}
                  </div>
                  <p className="text-amber-600/60 text-xs">
                    {t(role?.frenchName, role?.englishName)}
                    {p.statuses.includes("infected") && t(" (infecté)"," (infected)")}
                    {isRogueWC && t(" (loup renégat)"," (rogue wolf)")}
                  </p>
                </div>
                <span className="text-sm">{p.alive ? "✅" : "💀"}</span>
              </div>
            );
          })}
        </div>
      </Panel>
      <Btn variant="primary" onClick={() => dispatch({type:"GO_TO_HOME"})} className="w-full py-4 text-base">{t("🏠 Retour à l'accueil","🏠 Back to home")}</Btn>
    </motion.div>
  );
}
